import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { getState } from '@ngrx/signals';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { CheckAllComponent } from '../../atoms/check-all/check-all.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';

@Component({
  selector: 'app-year-checkbox-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CheckboxModule,
    FormsModule,
    DropdownModule,
    CheckAllComponent,
    ScrollPanelModule,
  ],
  templateUrl: './year-checkbox-group.component.html',
  styleUrl: './year-checkbox-group.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YearCheckboxGroupComponent),
      multi: true,
    },
  ],
})
export class YearCheckboxGroupComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  readonly layerStore = inject(DataLayerStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);

  @Input() label = 'Jahre';
  @Input() name = 'years';
  @Input() selection: (number | string)[] = [];
  @Input() dataLayerType?: DataLayerType;
  @Input() dataLayerTypeId = '';
  @Input() isStandalone = false;
  @Output() selectionChange = new EventEmitter<number[]>();

  years: number[] | null = null;
  typeLayers: DataLayer[] | null = null;
  touched = false;

  /**
   * Converts the `years` array to an array of strings.
   * If the `years` array is undefined or null, returns an empty array.
   *
   * @return {string[]} An array of strings converted from the `years` array or an empty array if `years` is not defined.
   */
  asStrings(): string[] {
    return this.years?.map(year => String(year)) ?? [];
  }

  constructor() {
    effect(() => {
      const layerState = getState(this.layerStore);
    });
  }

  /**
   * Checks if the given key is not part of the current selection.
   *
   * @param {number} key - The key to check against the selection.
   * @return {boolean} Returns true if the key is not in the selection; otherwise, false.
   */
  isInverseSelected(key: number): boolean {
    // Returns true if the key is NOT in the selection
    return !this.selection.includes(key);
  }

  /**
   * Toggles the selection state of an item identified by its key.
   * If the item is currently selected and the checkbox is checked, the item will be removed from the selection.
   * If the item is not currently selected and the checkbox is unchecked, the item will be added to the selection.
   *
   * @param {number} key - The unique identifier of the item to toggle in the selection.
   * @param {boolean} isChecked - The state of the associated checkbox. true if the checkbox is checked, false if unchecked.
   * @return {void} No return value.
   */
  toggleSelection(key: number, isChecked: boolean): void {
    if (isChecked) {
      // Remove from selection because the checkbox should act as "deselected"
      this.selection = this.selection.filter(item => item !== key);
    } else {
      // Add to selection because the checkbox is "selected"
      this.selection = [...this.selection, key];
    }

    // Notify about the change if necessary
    this.onChange(this.selection);
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * This method is triggered by Angular whenever the input properties of the component are updated.
   *
   * @param {SimpleChanges} changes - An object that holds the current and previous values of input properties that changed.
   *                                   This is used to track and respond to changes in specific input properties.
   * @return {void} Does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['dataLayerId']) {
      this.setYears();
    }
  }

  /**
   * Lifecycle hook that is called after Angular has initialized the component.
   * If `dataLayerType` is undefined or null, it triggers the `setYears` method
   * to initialize the necessary data for the component.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    if (!this.dataLayerType) {
      this.setYears();
    }
  }

  /**
   * Sets the years for the data layer type by retrieving and processing year information.
   *
   * This method assigns the `dataLayerType` based on the `dataLayerTypeId` from the `layerTypeStore`.
   * It then fetches and processes the years related to the `dataLayerTypeId` from the `layerStore`,
   * converting each year to a number and sorting them in descending order. Additionally, it filters
   * and assigns type layers associated with the `dataLayerTypeId`.
   *
   * @return {void} Does not return a value.
   */
  setYears(): void {
    this.dataLayerType = this.layerTypeStore.getById(this.dataLayerTypeId);
    this.years = this.layerStore
      .getYearsByParent(this.dataLayerTypeId)
      .map(year => Number(year))
      .sort((a, b) => b - a);
    this.typeLayers = this.layerStore.filterByParent(this.dataLayerTypeId);
  }

  /**
   * Handles the change event for a selection input by emitting the updated value after a slight delay.
   *
   * @param {Array<number | string>} value - An array containing numbers or strings representing the current selection.
   * The method maps string values to numbers and emits the updated array after processing.
   */
  protected onChange = (value: (number | string)[]) => {
    setTimeout(() => {
      this.selectionChange.emit(value.map(s => Number(s)));
    }, 10);
  };

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: any): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
